import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { authApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Icon,
  Input,
  PhoneInput,
  PageHeader,
  useToast,
} from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { errorMessage } from '@/hooks/useApiError';
import { formatDate, relativeTime } from '@/utils/format';
import { phoneProblem, toE164 } from '@/utils/phone';

/**
 * The admin's own account.
 *
 * Deliberately without the "close my account" action every other account page
 * has: a super admin deleting themselves can lock the platform out of its own
 * administration, and the recovery is a shell on the database server. Removing
 * an admin is done from Users, by a different admin, so there is always a
 * second pair of hands.
 */

/**
 * Friendly names for each permission domain.
 *
 * Grouping on the first segment rather than a hand-written prefix list means a
 * permission added to `app/core/permissions.py` lands in a sensible group on
 * its own; only its heading needs a line here, and an unmapped domain still
 * renders under its own raw name rather than disappearing into "Other".
 */
const DOMAIN_LABELS = {
  admin: 'Platform administration',
  company: 'Company workspaces',
  student: 'Student accounts',
  internship: 'Internships',
  application: 'Applications',
  candidate: 'Candidates',
  interview: 'Interviews',
  offer: 'Offers',
  subscription: 'Billing',
  message: 'Messaging',
  analytics: 'Reporting',
  ats: 'ATS integration',
};

/** Headings run in this order; anything unlisted follows, alphabetically. */
const DOMAIN_ORDER = Object.keys(DOMAIN_LABELS);

function groupPermissions(permissions) {
  const byDomain = new Map();
  for (const permission of permissions) {
    const domain = permission.split('.')[0];
    if (!byDomain.has(domain)) byDomain.set(domain, []);
    byDomain.get(domain).push(permission);
  }

  return [...byDomain.entries()]
    .sort(([a], [b]) => {
      const rankA = DOMAIN_ORDER.indexOf(a);
      const rankB = DOMAIN_ORDER.indexOf(b);
      if (rankA !== -1 && rankB !== -1) return rankA - rankB;
      if (rankA !== -1) return -1;
      if (rankB !== -1) return 1;
      return a.localeCompare(b);
    })
    .map(([domain, items]) => ({
      label: DOMAIN_LABELS[domain] ?? domain,
      items: items.sort(),
    }));
}

function initials(name) {
  if (!name) return 'A';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}

export function AdminProfilePage() {
  const { user, refreshUser } = useAuth();
  const toast = useToast();
  const { hash } = useLocation();

  // The account menu's "Change password" lands here as #password.
  useEffect(() => {
    if (hash === '#password') {
      document.getElementById('password')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [hash]);

  const [fullName, setFullName] = useState(user?.full_name ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [phoneError, setPhoneError] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const saveDetails = useMutation({
    mutationFn: () =>
      authApi.updateAccount({ full_name: fullName.trim(), phone: toE164(phone) || null }),
    onSuccess: async () => {
      await refreshUser();
      toast.success('Profile updated');
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not update the profile')),
  });

  const changePassword = useMutation({
    mutationFn: () => authApi.changePassword({ new_password: newPassword }),
    onSuccess: () => {
      toast.success('Password changed. Every other session was signed out.');
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not change the password')),
  });

  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const canChangePassword = newPassword.length > 0 && confirmPassword.length > 0 && !mismatch;

  const groups = groupPermissions(user?.permissions ?? []);

  return (
    <div>
      <PageHeader title="My profile" description="Your admin account and what it can do." />

      {/* -- identity --------------------------------------------------------- */}
      <Card className="mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <span className="bg-brand-600 grid size-14 shrink-0 place-items-center rounded-full text-lg font-semibold text-white">
            {initials(user?.full_name)}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-ink truncate text-lg font-semibold">{user?.full_name}</h2>
              <Badge tone="brand">Super admin</Badge>
              {user?.is_email_verified ? (
                <Badge tone="success">Email verified</Badge>
              ) : (
                <Badge tone="warning">Email unverified</Badge>
              )}
            </div>
            <p className="text-ink-muted truncate text-sm">{user?.email}</p>
          </div>
        </div>

        <dl className="border-line mt-6 grid gap-4 border-t pt-4 sm:grid-cols-3">
          <div>
            <dt className="text-ink-muted text-xs">Account status</dt>
            <dd className="text-ink mt-0.5 text-sm font-medium">{user?.status}</dd>
          </div>
          <div>
            <dt className="text-ink-muted text-xs">Admin since</dt>
            <dd className="text-ink mt-0.5 text-sm font-medium">
              {user?.created_at ? formatDate(user.created_at) : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-ink-muted text-xs">Last sign-in</dt>
            <dd className="text-ink mt-0.5 text-sm font-medium">
              {user?.last_login_at ? relativeTime(user.last_login_at) : '—'}
            </dd>
          </div>
        </dl>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* -- details -------------------------------------------------------- */}
        <Card>
          <CardHeader
            title="Details"
            description="How you appear in the audit log and to your team."
          />
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              const problem = phoneProblem(phone);
              setPhoneError(problem);
              if (!problem) saveDetails.mutate();
            }}
          >
            <Input
              label="Full name"
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              required
              minLength={2}
            />
            <PhoneInput
              label="Phone"
              hint="Optional"
              value={phone}
              error={phoneError}
              onChange={(next) => {
                setPhone(next);
                if (phoneError) setPhoneError(phoneProblem(next));
              }}
            />
            <Input
              label="Email"
              value={user?.email ?? ''}
              disabled
              hint="Changing the sign-in address of an admin account is not self-service."
            />
            <Button type="submit" isLoading={saveDetails.isPending}>
              Save changes
            </Button>
          </form>
        </Card>

        {/* -- password ------------------------------------------------------- */}
        <section id="password" className="scroll-mt-24">
          <Card>
            <CardHeader
              title="Password"
              description="Changing it signs out every other session, everywhere."
            />
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                changePassword.mutate();
              }}
            >
              <Input
                label="New password"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                required
                hint="At least 8 characters, with an upper case letter, a lower case letter and a number."
              />
              <Input
                label="Confirm new password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                error={mismatch ? 'The two passwords do not match.' : undefined}
                required
              />
              <Button
                type="submit"
                disabled={!canChangePassword}
                isLoading={changePassword.isPending}
              >
                Change password
              </Button>
            </form>
          </Card>
        </section>
      </div>

      {/* -- permissions ------------------------------------------------------ */}
      <Card className="mt-6">
        <CardHeader
          title="What this account can do"
          description="The effective permission set the API enforces on your requests."
        />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {groups.map((group) => (
            <div key={group.label}>
              <p className="text-ink mb-2 text-sm font-medium">{group.label}</p>
              <ul className="space-y-1">
                {group.items.map((permission) => (
                  <li
                    key={permission}
                    className="text-ink-muted flex items-start gap-1.5 font-mono text-xs"
                  >
                    <Icon name="verification" size={13} className="mt-0.5 text-success-600" />
                    {permission}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        {groups.length === 0 && (
          <EmptyState compact title="No permissions are attached to this account" />
        )}
      </Card>

      <Alert tone="info" className="mt-6" title="Closing an admin account">
        There is no self-service delete here on purpose. A super admin who deletes their own account
        can leave the platform with no administrator. Ask another admin to suspend or remove the
        account from Users instead.
      </Alert>
    </div>
  );
}
